/**
 * How many of one treat the menu draws, and whether tapping it spends from the bank.
 *
 * The six treats (👽 Survival, 💎 Pairs, 🧊 Ice Maze, 🛰️ Labyrinth, 🌌 Wagon, 🍝 Recipe) each have
 * their own bank in ReadingProgression, but the rule for turning a bank into icons is the same for
 * all six — and it has three overrides on it. Keeping that rule here rather than in MenuScene means
 * it is one function with one test, instead of six copies of an `if` inside a Phaser scene.
 *
 * See specs/word-mastery-progression.md §6.5 for the finished-lesson unlock, and src/game/dev.ts
 * for the two dev flags.
 */

export interface TreatSlots {
  /** Icons to draw. 0 = the treat is not shown at all. */
  count: number;
  /** A tap must NOT claim from the bank — the run is free. */
  free: boolean;
}

export interface TreatInput {
  /** Spendable treats in this lesson's bank (already capped by the engine's availableX()). */
  banked: number;
  /** The bank's display cap (UFO_MAX, DIAMOND_MAX, …). */
  cap: number;
  /** This lesson's journey is finished — every minigame is open for it. */
  journeyDone: boolean;
  /** `?treats`: fill every bank to its cap (dev/staging only). */
  showAll: boolean;
  /** The mode's own dev flag (`?pairs`, `?recipe`, …): show one, don't spend it. */
  forceOne: boolean;
}

/**
 * Four cases, in priority order:
 *
 *  - **`?treats`** fills the bank to its cap. It exists to answer "what does this menu look like at
 *    its fullest", which is a layout question that cannot be answered by playing — seeing every
 *    slot filled for real means earning six different treats to their caps in one profile.
 *  - **a finished lesson** shows exactly ONE, whatever the bank holds. One rather than the cap
 *    because the count has stopped meaning anything once the plays are unlimited: six icons then
 *    read as "all six minigames are open", where twenty-one would read as a hoard about to run out.
 *  - **the mode's own flag** shows ONE too, because that flag is for playing the mode rather than
 *    for looking at the menu.
 *  - otherwise, whatever the child has actually banked.
 *
 * All three overrides are free, and for the same reason in each case: nothing was earned toward
 * this particular run, so nothing may be taken for it. That is what keeps a finished lesson's
 * banked treats intact — they are still there, unspent, if the journey is ever reset.
 */
export function treatSlots({ banked, cap, journeyDone, showAll, forceOne }: TreatInput): TreatSlots {
  const free = showAll || journeyDone || forceOne;
  if (showAll) return { count: cap, free };
  if (journeyDone) return { count: 1, free };
  if (forceOne) return { count: Math.max(1, banked), free };
  return { count: banked, free };
}
